import { useEffect, useState } from "react";
import {
  adicionarDocumento,
  confirmarTribunal,
  lerDocumentosParaAdmin,
  lerTribunaisParaAdmin,
  salvarDocumento,
  salvarTribunal,
  type Documento,
  type TipoDeDocumento,
  type TribunalParaAdmin,
} from "../lib/forofacil";
import { CAMPO, Recado } from "./ui";

/**
 * Admin → ForoFácil: os 27 Tribunais de Justiça, os 6 TRFs e os documentos de
 * credenciamento de cada um.
 *
 * ## Por que esta tela existe
 *
 * No app de origem os 33 tribunais eram uma constante de 888 linhas, revisada
 * quatro vezes — todas em 18/03/2026 — e nunca mais. Link de portal de tribunal
 * muda sem aviso, e o levantamento de 02/09 achou o cadastro de Rondônia
 * apontando para o Tribunal ELEITORAL, um e-mail de Rondônia no Tocantins e um
 * DDD de Alagoas em Pernambuco. Corrigir exigia deploy; aqui é dado (000169), e
 * esta tela é onde a equipe abre o site do tribunal, confere e diz "conferi".
 *
 * ## A trilha de conferência, e por que ela morre sozinha
 *
 * "Salvar e confirmar links" grava os quatro endereços E carimba quem e quando
 * pelo servidor (RPC `foro_confirmar_tribunal`). Se qualquer link mudar depois,
 * por qualquer caminho, o gatilho da 000169 zera o carimbo: selo verde sobre
 * link que ninguém viu é pior do que nenhum selo. Enquanto o tribunal não é
 * conferido, o aluno vê "links por conferir pela equipe" e a nota de origem.
 *
 * ## O que a nota de origem É
 *
 * A PISTA: o que a origem não trouxe ou trouxe estranho. Ela não decide nada na
 * tela do aluno além do selo; aponta onde olhar. Os seis TRFs vieram com a
 * mesma lista genérica de 6 documentos e sem edital — a nota deles diz isso.
 *
 * ## Documento não se apaga
 *
 * O tique do aluno aponta para o `id` do documento. Apagar levaria os tiques por
 * cascade; por isso aqui só existe LIGAR/DESLIGAR, renomear e reordenar. E não
 * há botão de excluir tribunal pela mesma razão.
 *
 * ## O que esta tela NÃO decide
 *
 * Nada. Quem pode escrever é `pode_editar('forofacil')` (000297) — na policy e dentro da RPC. Quem
 * valida o link (https, sem `javascript:`) é a constraint. A tela mostra a
 * recusa do banco com as palavras do banco.
 */
export default function AdminForoFacil() {
  const [tribunais, setTribunais] = useState<TribunalParaAdmin[] | null>(null);
  const [documentos, setDocumentos] = useState<Documento[] | null>(null);
  const [erro, setErro] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [soPorConferir, setSoPorConferir] = useState(false);

  const recarregar = async () => {
    const [t, d] = await Promise.all([lerTribunaisParaAdmin(), lerDocumentosParaAdmin()]);
    if (!t || !d) {
      setErro(true);
      return;
    }
    setErro(false);
    setTribunais(t);
    setDocumentos(d);
  };

  useEffect(() => {
    void recarregar();
  }, []);

  const aviso = (tipo: "ok" | "erro", texto: string) => {
    setRecado({ tipo, texto });
    // O recado de acerto some sozinho; o de erro fica até a próxima ação.
    if (tipo === "ok") window.setTimeout(() => setRecado(null), 4000);
  };

  if (erro) {
    return (
      <Recado
        tipo="erro"
        texto="Não foi possível ler o catálogo de tribunais. Recarregue a página; se continuar, o banco pode estar fora do ar."
      />
    );
  }
  if (!tribunais || !documentos) return <p className="text-sm text-cinza">Carregando o catálogo…</p>;

  const conferidos = tribunais.filter((t) => t.conferido_em).length;
  const porConferir = tribunais.length - conferidos;
  const visiveis = soPorConferir ? tribunais.filter((t) => !t.conferido_em) : tribunais;

  return (
    <div className="space-y-8">
      <header>
        <h2 className="font-display text-xl font-bold text-titulo">ForoFácil — os tribunais</h2>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-cinza">
          Os 27 Tribunais de Justiça e os 6 TRFs: os links de cadastro como perito e a lista de
          documentos que cada um pede. Toda alteração vale <strong>na hora</strong> para os
          alunos da Perícia. Para conferir um tribunal: abra os links, veja se cada um leva ao
          lugar certo, corrija o que mudou e clique em <strong>Salvar e confirmar links</strong>.
          A <em>nota de origem</em> diz o que veio estranho do ForoFácil antigo.
        </p>
        <p
          id="resumo-conferencia"
          data-conferidos={conferidos}
          data-por-conferir={porConferir}
          className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${
            porConferir === 0 ? "bg-aviso-fundo text-aviso-tinta" : "bg-alerta-fundo text-alerta-tinta"
          }`}
        >
          {conferidos} de {tribunais.length} conferidos
          {porConferir > 0 && <span className="font-normal">· {porConferir} por conferir</span>}
        </p>
      </header>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h3 className="font-display text-base font-bold text-titulo">Os 33 tribunais</h3>
          <label className="flex items-center gap-2 text-sm text-tinta">
            <input
              type="checkbox"
              checked={soPorConferir}
              onChange={(e) => setSoPorConferir(e.target.checked)}
              className="size-4 accent-laranja"
            />
            Mostrar só os por conferir
          </label>
        </div>

        <ul className="mt-4 space-y-3">
          {/* A chave carrega o que a linha mostra: depois de salvar, ela REMONTA
              com o dado novo do banco em vez de ficar com o estado local. */}
          {visiveis.map((t) => (
            <li key={`${t.sigla}:${t.conferido_em ?? ""}:${t.nome}:${t.sistema}`}>
              <LinhaDeTribunal
                tribunal={t}
                documentos={documentos.filter((d) => d.tribunal === t.sigla)}
                onSalvo={(texto) => {
                  aviso("ok", texto);
                  void recarregar();
                }}
                onErro={(texto) => aviso("erro", texto)}
              />
            </li>
          ))}
          {visiveis.length === 0 && (
            <li className="surface p-5 text-sm text-cinza">
              Todos os {tribunais.length} tribunais estão conferidos.
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}

function LinhaDeTribunal({
  tribunal: t,
  documentos,
  onSalvo,
  onErro,
}: {
  tribunal: TribunalParaAdmin;
  documentos: Documento[];
  onSalvo: (texto: string) => void;
  onErro: (texto: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [links, setLinks] = useState({
    site: t.site,
    link_cadastro: t.link_cadastro,
    edital: t.edital ?? "",
    tutorial: t.tutorial ?? "",
  });
  const [dados, setDados] = useState({
    nome: t.nome,
    nome_completo: t.nome_completo,
    sistema: t.sistema,
    alerta: t.alerta ?? "",
    contato: t.contato ?? "",
    nota_de_origem: t.nota_de_origem ?? "",
  });
  const [ocupado, setOcupado] = useState<"links" | "dados" | null>(null);

  async function confirmar() {
    setOcupado("links");
    const erro = await confirmarTribunal(t.sigla, links);
    setOcupado(null);
    if (erro) onErro(`${t.sigla}: o banco recusou — ${erro}`);
    else onSalvo(`${t.nome_completo}: links salvos e conferência carimbada.`);
  }

  async function salvarDados() {
    setOcupado("dados");
    const erro = await salvarTribunal(t.sigla, {
      nome: dados.nome.trim(),
      nome_completo: dados.nome_completo.trim(),
      sistema: dados.sistema.trim(),
      alerta: dados.alerta.trim() || null,
      contato: dados.contato.trim() || null,
      nota_de_origem: dados.nota_de_origem.trim() || null,
    });
    setOcupado(null);
    if (erro) onErro(`${t.sigla}: o banco recusou — ${erro}`);
    else onSalvo(`${t.nome_completo}: dados salvos.`);
  }

  const campo = (rotulo: string, valor: string, onChange: (v: string) => void, dica?: string) => (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.09em] text-cinza">
        {rotulo}
      </span>
      <input
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        placeholder={dica}
        className={`${CAMPO} h-10 text-sm`}
      />
    </label>
  );

  return (
    <article className="surface p-4 sm:p-5" data-tribunal-admin={t.sigla}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-bold text-cinza">{t.sigla}</span>
            <span className="font-display text-base font-bold text-titulo">{t.nome_completo}</span>
            <span className="rounded-full bg-chip px-2 py-0.5 text-[11px] font-semibold text-chip-tinta">
              {t.tipo === "tj" ? "TJ" : "TRF"} · {t.sistema}
            </span>
          </p>
          <p className="mt-1 text-xs text-cinza">
            {t.conferido_em ? (
              <>
                Conferido em {new Date(t.conferido_em).toLocaleDateString("pt-BR")} por{" "}
                {t.conferidor?.nome ?? "equipe"} · {documentos.filter((d) => d.ativo).length} documentos ligados
              </>
            ) : (
              <>
                <span className="font-semibold text-alerta-tinta">Por conferir.</span>{" "}
                {documentos.filter((d) => d.ativo).length} documentos ligados.
              </>
            )}
          </p>
          {t.nota_de_origem && (
            <p className="mt-2 max-w-3xl rounded-cf bg-superficie-2 p-2.5 text-xs leading-relaxed text-tinta">
              <span className="font-semibold">Nota de origem:</span> {t.nota_de_origem}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setAberto((a) => !a)}
          aria-expanded={aberto}
          className="btn-contorno text-sm"
        >
          {aberto ? "Fechar" : "Editar"}
        </button>
      </div>

      {aberto && (
        <div className="mt-5 space-y-6">
          <section>
            <h4 className="font-display text-sm font-bold text-titulo">Os quatro links</h4>
            <p className="mt-0.5 text-xs text-cinza">
              Todos <code>https://</code>. Edital e tutorial em branco = o tribunal não publica.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {campo("Site oficial", links.site, (v) => setLinks({ ...links, site: v }))}
              {campo("Link de cadastro", links.link_cadastro, (v) => setLinks({ ...links, link_cadastro: v }))}
              {campo("Edital", links.edital, (v) => setLinks({ ...links, edital: v }), "em branco = não publica")}
              {campo("Tutorial", links.tutorial, (v) => setLinks({ ...links, tutorial: v }), "em branco = não publica")}
            </div>
            <button
              type="button"
              onClick={confirmar}
              disabled={ocupado !== null}
              className="pill-cta mt-3 disabled:opacity-60"
            >
              {ocupado === "links" ? "Confirmando…" : "Salvar e confirmar links"}
            </button>
          </section>

          <section>
            <h4 className="font-display text-sm font-bold text-titulo">Dados do tribunal</h4>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {campo("Nome curto", dados.nome, (v) => setDados({ ...dados, nome: v }))}
              {campo("Nome completo", dados.nome_completo, (v) => setDados({ ...dados, nome_completo: v }))}
              {campo("Sistema", dados.sistema, (v) => setDados({ ...dados, sistema: v }))}
              {campo("Contato", dados.contato, (v) => setDados({ ...dados, contato: v }))}
            </div>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.09em] text-cinza">
                Alerta ao aluno
              </span>
              <textarea
                value={dados.alerta}
                onChange={(e) => setDados({ ...dados, alerta: e.target.value })}
                rows={2}
                className={`${CAMPO} h-auto py-2 text-sm`}
              />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.09em] text-cinza">
                Nota de origem (a pista da conferência)
              </span>
              <textarea
                value={dados.nota_de_origem}
                onChange={(e) => setDados({ ...dados, nota_de_origem: e.target.value })}
                rows={2}
                className={`${CAMPO} h-auto py-2 text-sm`}
              />
            </label>
            <button
              type="button"
              onClick={salvarDados}
              disabled={ocupado !== null}
              className="btn-contorno mt-3 text-sm disabled:opacity-60"
            >
              {ocupado === "dados" ? "Salvando…" : "Salvar dados"}
            </button>
          </section>

          <ListaDeDocumentosAdmin
            tribunal={t.sigla}
            tipo="pf"
            documentos={documentos.filter((d) => d.tipo === "pf")}
            onSalvo={onSalvo}
            onErro={onErro}
          />
          <ListaDeDocumentosAdmin
            tribunal={t.sigla}
            tipo="pj"
            documentos={documentos.filter((d) => d.tipo === "pj")}
            onSalvo={onSalvo}
            onErro={onErro}
          />
        </div>
      )}
    </article>
  );
}

function ListaDeDocumentosAdmin({
  tribunal,
  tipo,
  documentos,
  onSalvo,
  onErro,
}: {
  tribunal: string;
  tipo: TipoDeDocumento;
  documentos: Documento[];
  onSalvo: (texto: string) => void;
  onErro: (texto: string) => void;
}) {
  const [novo, setNovo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const ordenados = [...documentos].sort((a, b) => a.ordem - b.ordem);

  async function adicionar() {
    if (!novo.trim()) return;
    setOcupado(true);
    const proximaOrdem = (ordenados.at(-1)?.ordem ?? 0) + 10;
    const erro = await adicionarDocumento(tribunal, tipo, novo, proximaOrdem);
    setOcupado(false);
    if (erro) onErro(`${tribunal}: o banco recusou o documento — ${erro}`);
    else {
      setNovo("");
      onSalvo(`${tribunal}: documento acrescentado à lista ${tipo.toUpperCase()}.`);
    }
  }

  return (
    <section>
      <h4 className="font-display text-sm font-bold text-titulo">
        Documentos — {tipo === "pf" ? "pessoa física" : "pessoa jurídica"}{" "}
        <span className="font-normal text-cinza">
          · {ordenados.filter((d) => d.ativo).length} ligados
          {ordenados.some((d) => !d.ativo) && `, ${ordenados.filter((d) => !d.ativo).length} desligados`}
        </span>
      </h4>
      <p className="mt-0.5 text-xs text-cinza">
        Documento não se apaga (o tique do aluno aponta para ele): desligue, renomeie ou reordene.
      </p>
      <ul className="mt-3 space-y-1.5">
        {ordenados.map((d) => (
          <LinhaDeDocumento key={`${d.id}:${d.texto}:${d.ordem}:${d.ativo}`} documento={d} onSalvo={onSalvo} onErro={onErro} />
        ))}
        {ordenados.length === 0 && (
          <li className="text-sm text-cinza">Nenhum documento cadastrado nesta lista.</li>
        )}
      </ul>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="block min-w-0 flex-1">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.09em] text-cinza">
            Acrescentar documento
          </span>
          <input
            value={novo}
            onChange={(e) => setNovo(e.target.value)}
            placeholder="Ex.: Certidão negativa de débitos federais"
            className={`${CAMPO} h-10 text-sm`}
          />
        </label>
        <button
          type="button"
          onClick={adicionar}
          disabled={ocupado || !novo.trim()}
          className="btn-contorno text-sm disabled:opacity-60"
        >
          {ocupado ? "Salvando…" : "Acrescentar"}
        </button>
      </div>
    </section>
  );
}

function LinhaDeDocumento({
  documento: d,
  onSalvo,
  onErro,
}: {
  documento: Documento;
  onSalvo: (texto: string) => void;
  onErro: (texto: string) => void;
}) {
  const [texto, setTexto] = useState(d.texto);
  const [ordem, setOrdem] = useState(String(d.ordem));
  const [ocupado, setOcupado] = useState(false);
  const mudou = texto.trim() !== d.texto || Number(ordem) !== d.ordem;

  async function salvar(campos: Partial<Pick<Documento, "texto" | "ordem" | "ativo">>, oQue: string) {
    setOcupado(true);
    const erro = await salvarDocumento(d.id, campos);
    setOcupado(false);
    if (erro) onErro(`${d.tribunal}: o banco recusou — ${erro}`);
    else onSalvo(`${d.tribunal}: ${oQue}.`);
  }

  return (
    <li className={`flex flex-wrap items-center gap-2 rounded-lg border border-borda p-2 ${d.ativo ? "" : "opacity-60"}`}>
      <input
        value={ordem}
        onChange={(e) => setOrdem(e.target.value.replace(/\D/g, ""))}
        aria-label="Ordem"
        className={`${CAMPO} h-9 w-16 px-2 text-center text-xs`}
      />
      <input
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        aria-label="Texto do documento"
        className={`${CAMPO} h-9 min-w-0 flex-1 text-sm`}
      />
      {mudou && (
        <button
          type="button"
          disabled={ocupado || !texto.trim()}
          onClick={() => salvar({ texto: texto.trim(), ordem: Number(ordem) || 0 }, "documento salvo")}
          className="btn-contorno h-9 px-3 text-xs disabled:opacity-60"
        >
          Salvar
        </button>
      )}
      <button
        type="button"
        disabled={ocupado}
        onClick={() => salvar({ ativo: !d.ativo }, d.ativo ? "documento desligado" : "documento religado")}
        className="btn-contorno h-9 px-3 text-xs disabled:opacity-60"
      >
        {d.ativo ? "Desligar" : "Religar"}
      </button>
    </li>
  );
}
